import { Component, Inject, OnInit } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { select, Store } from "@ngrx/store";
import { jsPDF } from "jspdf";
import * as moment from "moment";
import { combineLatest, of, Subscription } from "rxjs";
import { delay, filter, switchMap, withLatestFrom } from "rxjs/operators";
import { SignInState } from "src/app/components/allusers/signin/signin.model";
import { getSignedInUser } from "src/app/components/allusers/signin/signin.selector";
import { Certificate } from "src/app/models/certificate.model";
import { User } from "src/app/models/user.model";
import { CertificatesService } from "src/app/services/certificates.service";
import { CoursesService } from "src/app/services/courses.service";
import { RegmembersService } from "src/app/services/regmembers.service";
import { CourseState } from "../courses/courses.reducer";
import { getSelectedCourseState, isCourseNotSelected } from "../courses/courses.selector";
import * as data from "./data.json";

@Component({
  selector: "app-download-certificate",
  templateUrl: "./download-certificate.component.html",
  providers: [{ provide: "Window", useValue: window }],
  styleUrls: ["./download-certificate.component.css"],
})
export class DownloadCertificateComponent implements OnInit {
  certificateId;
  certificate: Certificate;
  errorMessage;

  currentUser$ = this.signInStore.pipe(
    select(getSignedInUser),
    filter((val) => (val ? true : false))
  );
  currentUser: User;

  activatedRoute$ = this.activatedRoute.params.pipe(
    switchMap(params => {
      return of(params).pipe(
        withLatestFrom(this.courseStore.pipe(select(getSelectedCourseState))),
        switchMap(([p, course]) => {
          if (!course) {
            return of(p).pipe(delay(500));
          }
          return of(p);
        })
      );
    }),
    withLatestFrom(this.courseStore.pipe(select(getSelectedCourseState))), // Get selected course
    filter(([params, course]) => course !== null && course !== undefined) // Ensure course exists
  );


  courseSelected$ = this.courseStore.pipe(select(getSelectedCourseState), filter(val => !val));
  courseNotSelected$ = this.courseStore.pipe(select(isCourseNotSelected), filter(val => val))
  currentCourse: CourseState;

  subscriptions$: Array<Subscription> = [];

  constructor(
    private signInStore: Store<SignInState>,
    private certificatesService: CertificatesService,
    private coursesService: CoursesService,
    private activatedRoute: ActivatedRoute,
    private regmemService: RegmembersService,
    @Inject("Window") private window: Window,
    private courseStore: Store<CourseState>
  ) { }

  async ngOnInit() {
    this.subscriptions$.push(
      combineLatest([this.activatedRoute$, this.currentUser$]).subscribe(
        async ([[params, courseState], user]) => {
          this.currentCourse = courseState;
          this.currentUser = user;
          await this.getCertificate();
        }
      )
    );
  }

  async download() {
    // var doc = new jsPDF();
    const doc = new jsPDF({
      orientation: "landscape",
      unit: "mm",
      format: "a4"
    });

    const { certTemplate } = data;

    doc.addImage(certTemplate, "PNG", 0, 0, 296, 209);
    doc.setFontSize(22);
    const nameXOffset = (doc.internal.pageSize.getWidth() - doc.getTextWidth(this.certificate.cert_name)) / 2;
    doc.text(this.certificate.cert_name, nameXOffset, 105);
    doc.setFontSize(13);
    
    const dateXOffset = (doc.internal.pageSize.getWidth() - (doc.getTextWidth(moment(this.certificate.cert_dt).format("MM-DD-YYYY"))) / 2) / 2 - 8;
    const options = {
      year: 'numeric',
      month: 'long', // 'long' gives the full month name like "August"
      day: 'numeric',
    } as Intl.DateTimeFormatOptions;
    
    const date = new Date(this.certificate.cert_dt);
    const formattedDate = new Intl.DateTimeFormat('en-US', options).format(date);
    doc.text(formattedDate, dateXOffset, 134);
    const courseDesc = this.currentCourse.course.certificateDesc || 'has completed Keystodriving.com 30 Hour Online Theory Course';
    const courseDescXOffset = (doc.internal.pageSize.getWidth() - doc.getTextWidth(courseDesc)) / 2;
    doc.setFont("sans-serif", "italic");
    doc.setFontSize(15);
    doc.setTextColor(105, 105, 105);
    doc.text(courseDesc, courseDescXOffset - 5, 120);

    let licenseNumber = this.currentCourse.course.licenseId;
    if (!licenseNumber && this.currentUser?.licenseinstate) {
      const courses = await this.coursesService.findAll();
      const stateCourse = courses.find(c => c.courseCode?.toUpperCase() === this.currentUser.licenseinstate.toUpperCase());
      licenseNumber = stateCourse?.licenseId;
    }
    if (licenseNumber) {
      const licenseXOffset = (doc.internal.pageSize.getWidth() - doc.getTextWidth(licenseNumber)) - 90;
      doc.text("License Number:", licenseXOffset - 5, 159);
      doc.setTextColor(169 / 255, 169 / 255, 169 / 255);
      doc.setFontSize(13);

      doc.text(licenseNumber, licenseXOffset, 166);
      doc.setFontSize(15);
      doc.setTextColor(0, 0, 0);
    }
    doc.save("certificate.pdf");
  }

  async getCertificate() {
    try {
      const certificates = await this.certificatesService
        .getCertificateByCourseIdAndMemberId(this.currentCourse.course.id, this.currentUser.memberid);
      if (certificates.length > 0) {
        this.certificate = certificates[0];
      }
    } catch (error) {
      this.errorMessage =
        "Unable to retrieve your certificate, please contact us directly";
    }
  }
}
